import React, { useEffect, useState, useCallback } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getOverview, parseReport } from '../api';
import KpiCard from '../components/KpiCard';
import ChartCard from '../components/ChartCard';
import ChartTooltip from '../components/ChartTooltip';

function fmt(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return Math.round(n).toString();
}
function fmtPct(n) { return (n * 100).toFixed(1) + '%'; }
function fmtDur(s) {
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
}

export default function Overview({ propertyId, startDate, endDate }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({});

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await getOverview(propertyId, startDate, endDate);
      const rows = parseReport(res.data).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setData(rows.map(r => ({
        ...r,
        date: r.date ? `${r.date.slice(4, 6)}/${r.date.slice(6, 8)}` : '',
        bounceRate: parseFloat((r.bounceRate * 100).toFixed(1)),
        engagementRate: parseFloat((r.engagementRate * 100).toFixed(1)),
      })));

      const tot = rows.reduce((acc, r) => ({
        activeUsers: (acc.activeUsers || 0) + r.activeUsers,
        sessions: (acc.sessions || 0) + r.sessions,
        screenPageViews: (acc.screenPageViews || 0) + r.screenPageViews,
        newUsers: (acc.newUsers || 0) + r.newUsers,
        eventCount: (acc.eventCount || 0) + r.eventCount,
        bounceRate: (acc.bounceRate || 0) + r.bounceRate,
        averageSessionDuration: (acc.averageSessionDuration || 0) + r.averageSessionDuration,
        engagementRate: (acc.engagementRate || 0) + r.engagementRate,
        _count: (acc._count || 0) + 1,
      }), {});
      if (tot._count) {
        tot.bounceRate /= tot._count;
        tot.averageSessionDuration /= tot._count;
        tot.engagementRate /= tot._count;
      }
      setKpis(tot);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;

  return (
    <div>
      <div style={styles.kpiGrid}>
        <KpiCard label="Active Users"   value={fmt(kpis.activeUsers || 0)}            icon="👥" color="var(--accent)" />
        <KpiCard label="Sessions"       value={fmt(kpis.sessions || 0)}               icon="🔄" color="var(--accent2)" />
        <KpiCard label="Page Views"     value={fmt(kpis.screenPageViews || 0)}        icon="📄" color="var(--accent3)" />
        <KpiCard label="New Users"      value={fmt(kpis.newUsers || 0)}               icon="✨" color="var(--accent4)" />
        <KpiCard label="Avg Session"    value={fmtDur(kpis.averageSessionDuration || 0)} icon="⏱" color="var(--accent5)" />
        <KpiCard label="Bounce Rate"    value={fmtPct(kpis.bounceRate || 0)}          icon="↩" color="var(--danger)" />
        <KpiCard label="Engagement"     value={fmtPct(kpis.engagementRate || 0)}      icon="💡" color="var(--success)" />
        <KpiCard label="Events"         value={fmt(kpis.eventCount || 0)}             icon="⚡" color="var(--warning)" />
      </div>

      <div style={styles.charts}>
        <ChartCard title="Users & Sessions Over Time" subtitle="Daily trend" span={2}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="uGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="sGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              <Area type="monotone" dataKey="activeUsers" stroke="#38bdf8" fill="url(#uGrad)" strokeWidth={2} name="Active Users" dot={false} />
              <Area type="monotone" dataKey="sessions"    stroke="#818cf8" fill="url(#sGrad)" strokeWidth={2} name="Sessions"     dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Engagement & Bounce Rate" subtitle="Daily %">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} unit="%" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              <Line type="monotone" dataKey="engagementRate" stroke="#34d399" strokeWidth={2} name="Engagement %" dot={false} />
              <Line type="monotone" dataKey="bounceRate"     stroke="#f87171" strokeWidth={2} name="Bounce %"     dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Page Views Over Time" subtitle="Daily screenPageViews">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="screenPageViews" stroke="#34d399" fill="url(#pvGrad)" strokeWidth={2} name="Page Views" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, color: 'var(--accent)', animation: 'spin 1s linear infinite' }}>◈</div>
        <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 8 }}>Loading analytics data...</div>
      </div>
    </div>
  );
}

const styles = {
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16, marginBottom: 24 },
  charts:  { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
};
